import { type ChangeEvent, useEffect, useState } from 'react'

import { onCheckDisabledConditions } from '@/app/utils'

interface IField {
    name: string
    value: string
    placeholder: string
    error: string | null
    isTouched: boolean
}

export const useFields = () => {
    const initialLogin = { name: 'login', value: '', placeholder: 'Логин', error: null, isTouched: false }
    const initialPassword = { name: 'password', value: '', placeholder: 'Пароль', error: null, isTouched: false }
    const initialConfirmPassword = {
        name: 'confirmPassword',
        value: '',
        placeholder: 'Подтвердите Пароль',
        error: null,
        isTouched: false
    }

    const [login, setLogin] = useState<IField>(initialLogin)
    const [password, setPassword] = useState<IField>(initialPassword)
    const [confirmPassword, setConfirmPassword] = useState<IField>(initialConfirmPassword)

    useEffect(() => {
        if (!login.value.length) {
            setLogin((prev) => ({ ...prev, error: 'Обязательное поле' }))
        } else if (login.value.length > 32) {
            setLogin((prev) => ({ ...prev, error: 'Максимальная длина 32 символа' }))
        } else if (!/^[a-zA-Z_]*$/gi.test(login.value[0])) {
            setLogin((prev) => ({ ...prev, error: 'Первый символ должен быть латинской буквой или "_"' }))
        } else if (!/^[a-zA-Z0-9_]*$/gi.test(login.value)) {
            setLogin((prev) => ({ ...prev, error: 'Доступны только числа, латиница и "_"' }))
        } else {
            setLogin((prev) => ({ ...prev, error: null }))
        }

        if (!password.value.length) {
            setPassword((prev) => ({ ...prev, error: 'Обязательное поле' }))
        } else if (!/^(?=.*\d)(?=.*[a-z])(?=.*[A-Z])(?=.*[!@#$;^&*])[0-9a-zA-Z!@#$;^&*]{8,25}$/g.test(password.value)) {
            setPassword((prev) => ({ ...prev, error: 'Пароль не валиден' }))
        } else {
            setPassword((prev) => ({ ...prev, error: null }))
        }

        if (!confirmPassword.value.length) {
            setConfirmPassword((prev) => ({ ...prev, error: 'Обязательное поле' }))
        } else if (confirmPassword.value !== password.value) {
            setConfirmPassword((prev) => ({ ...prev, error: 'Не соответствует паролю' }))
        } else {
            setConfirmPassword((prev) => ({ ...prev, error: null }))
        }
    }, [login.value, password.value, confirmPassword.value])

    const onInitializeFields = () => {
        setLogin(initialLogin)
        setPassword(initialPassword)
        setConfirmPassword(initialConfirmPassword)
    }

    const onChangeLogin = (e: ChangeEvent<HTMLInputElement>) => setLogin((prev) => ({ ...prev, value: e.target.value }))
    const onChangePassword = (e: ChangeEvent<HTMLInputElement>) =>
        setPassword((prev) => ({ ...prev, value: e.target.value }))
    const onChangeConfirmPassword = (e: ChangeEvent<HTMLInputElement>) =>
        setConfirmPassword((prev) => ({ ...prev, value: e.target.value }))

    const onTouchLogin = () => setLogin((prev) => ({ ...prev, isTouched: true }))
    const onTouchPassword = () => setPassword((prev) => ({ ...prev, isTouched: true }))
    const onTouchConfirmPassword = () => setConfirmPassword((prev) => ({ ...prev, isTouched: true }))

    const onZeroLogin = () => setLogin((prev) => ({ ...prev, value: '' }))
    const onZeroPassword = () => setPassword((prev) => ({ ...prev, value: '' }))
    const onZeroConfirmPassword = () => setConfirmPassword((prev) => ({ ...prev, value: '' }))

    const loginError = login.isTouched && login.error ? login.error : null
    const passwordError = password.isTouched && password.error ? password.error : null
    const confirmPasswordError = confirmPassword.isTouched && confirmPassword.error ? confirmPassword.error : null

    const isValidFields = onCheckDisabledConditions(
        Boolean(!login.error),
        Boolean(login.isTouched),
        Boolean(!password.error),
        Boolean(password.isTouched),
        Boolean(!confirmPassword.error),
        Boolean(confirmPassword.isTouched)
    )

    return {
        login,
        loginError,
        onChangeLogin,
        onTouchLogin,
        onZeroLogin,
        password,
        passwordError,
        onChangePassword,
        onTouchPassword,
        onZeroPassword,
        confirmPassword,
        confirmPasswordError,
        onChangeConfirmPassword,
        onTouchConfirmPassword,
        onZeroConfirmPassword,
        isValidFields,
        onInitializeFields
    }
}
